import { useState, useEffect, useRef, useCallback } from 'react';

interface StickyScrollOptions {
  offset?: number;
  containerSelector?: string;
  disableOnMobile?: boolean;
}

interface StickyScrollReturn {
  isSticky: boolean;
  stickyRef: React.RefObject<HTMLDivElement>;
  containerHeight: number;
  scrollProgress: number;
  isNearTop: boolean;
  isNearBottom: boolean;
}

export function useStickyScroll(options: StickyScrollOptions = {}): StickyScrollReturn {
  const {
    offset = 96, // 6rem in pixels (24 * 4)
    containerSelector = 'main',
    disableOnMobile = true
  } = options;

  const [isSticky, setIsSticky] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isNearTop, setIsNearTop] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const stickyRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement | null>(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Find container element
  useEffect(() => {
    const container = document.querySelector(containerSelector) as HTMLElement;
    containerRef.current = container;
    
    if (container) {
      setContainerHeight(container.offsetHeight);
    }
  }, [containerSelector]);

  const handleScroll = useCallback(() => {
    if (!stickyRef.current || !containerRef.current || (disableOnMobile && isMobile)) {
      setIsSticky(false);
      return;
    }

    const element = stickyRef.current;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const elementHeight = element.offsetHeight;
    const containerTop = containerRect.top + scrollY;
    const containerBottom = containerTop + containerRect.height;
    
    // Calculate if element should be sticky
    const shouldBeSticky = scrollY + offset > containerTop && 
                          scrollY < containerBottom - elementHeight - offset;
    
    setIsSticky(shouldBeSticky);
    
    // Calculate scroll progress within the container
    if (containerRect.height > 0) {
      const progress = Math.max(0, Math.min(1, 
        (scrollY - containerTop) / (containerRect.height - windowHeight)
      ));
      setScrollProgress(progress);
    }
    
    // Check if near top or bottom
    setIsNearTop(scrollY < containerTop + 200);
    setIsNearBottom(scrollY > containerBottom - windowHeight - 200);
    
    // Update container height
    setContainerHeight(container.offsetHeight);
  }, [offset, disableOnMobile, isMobile]);

  useEffect(() => {
    // Initial check
    handleScroll();
    
    // Throttled scroll handler
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', handleScroll);
    };
  }, [handleScroll]);

  return {
    isSticky: isSticky && !isMobile,
    stickyRef,
    containerHeight,
    scrollProgress,
    isNearTop,
    isNearBottom
  };
}